import { useSeo } from "@/lib/seo";
import { IMG, SCHOLAR_URL, TEAM, INSTITUTIONS } from "@/lib/constants";
import { Overline, Reveal, CTAButton, Section } from "@/components/shared";
import { FlowDiagram, StateTransition } from "@/components/Diagrams";
import { Boxes, Waypoints, Lightbulb, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const pillars = [
  { title: "Independent Research", desc: "Computational models of aging and living systems.", to: "/independent-research" },
  { title: "Research Teams", desc: "Mathematical and computational modeling for complex research questions.", to: "/for-research-teams" },
  { title: "SMEs", desc: "Digital strategy, automation and systems-based business support.", to: "/for-smes" },
];

const whatWeDo = [
  { icon: Boxes, title: "Model", desc: "We translate complex systems into mathematical, computational and conceptual structures." },
  { icon: Waypoints, title: "Simulate", desc: "We explore scenarios, perturbations, transitions and possible futures." },
  { icon: Lightbulb, title: "Interpret", desc: "We turn model behavior into clear insight, hypotheses, decisions and next steps." },
];

const paths = [
  { tag: "01 / Research", title: "Aging & Living Systems", desc: "Independent research on cellular aging, resilience, senescence, apoptosis and biological transitions.", cta: "Explore Research", to: "/independent-research" },
  { tag: "02 / Consulting", title: "Scientific Research Teams", desc: "Support for researchers who need mathematical modeling, simulations, network analysis, scenario testing or methodological consultation.", cta: "For Research Teams", to: "/for-research-teams" },
  { tag: "03 / Consulting", title: "Small & Medium Enterprises", desc: "Integrated strategy, digital ecosystems, websites, automations and ongoing technical support for growing businesses.", cta: "For SMEs", to: "/for-smes" },
];

const why = [
  { t: "Complex systems perspective", d: "We treat problems as networks of interacting parts, not isolated variables." },
  { t: "Mathematical & computational rigor", d: "Assumptions are made explicit, formal and testable." },
  { t: "Practical interpretation, not just theory", d: "Models exist to inform decisions and next steps." },
  { t: "Independent thinking with real implementation", d: "We connect scientific reasoning, modeling, technology and implementation." },
];

export default function Home() {
  useSeo(
    "Systems Beyond | Complex Systems Solutions",
    "Systems Beyond helps research teams and businesses understand complex challenges through mathematical modeling, scientific consulting, digital strategy and practical implementation."
  );

  return (
    <div>
      {/* HERO */}
      <Section className="pt-36 pb-20 lg:pt-44 lg:pb-28" grid>
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <Overline>Independent research & consulting</Overline>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-7 text-5xl lg:text-7xl tracking-tighter leading-[0.95] font-medium text-ink">
                Complex systems,<br />made <span className="italic">understandable.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-8 text-base lg:text-lg text-dark-gray leading-relaxed max-w-xl">
                Systems Beyond is an independent research lab and consultancy using mathematical
                modeling, computational simulation and systems thinking to understand complex
                problems — from cellular aging to research strategy and business ecosystems.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-10 flex flex-wrap gap-3">
                <CTAButton to="/independent-research" variant="accent" testId="hero-explore-research">Explore Independent Research</CTAButton>
                <CTAButton to="/contact" variant="secondary" testId="hero-book-consultation">Book a Consultation</CTAButton>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={200}>
              <div className="relative border border-silver/50 bg-off-white">
                <img src={IMG.hero} alt="Abstract network overlaid on a cellular structure, representing biology and computation" className="w-full h-auto" />
                <span className="absolute top-3 left-3 font-mono-sci text-[10px] text-dark-gray">FIG.01 — SYSTEM / NETWORK</span>
                <span className="absolute bottom-3 right-3 w-3 h-3 border-t border-r border-acid" />
                <span className="absolute top-3 right-3 w-3 h-3 border-t border-r border-charcoal/30" />
              </div>
            </Reveal>
          </div>
        </div>

        {/* three pillar cards */}
        <div className="mt-16 grid md:grid-cols-3 gap-px bg-silver/40 border border-silver/40">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <Link to={p.to} className="block bg-white p-8 h-full group hover:bg-off-white transition-colors" data-testid={`pillar-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-sci text-xs text-blue-gray">0{i + 1}</span>
                  <ArrowRight size={16} className="text-silver group-hover:text-acid group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="mt-6 text-xl text-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-dark-gray leading-relaxed">{p.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CENTRAL IDEA */}
      <Section dark className="py-24 lg:py-32" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline dark>The central idea</Overline>
            <h2 className="mt-6 text-3xl lg:text-5xl tracking-tight leading-tight font-medium">
              Reality is built from interactions.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Cells, organisms, research programs and businesses do not behave as isolated parts.
              Their behavior emerges from relationships, feedback loops, thresholds, constraints,
              adaptation and nonlinear change.
            </p>
            <p className="mt-4 text-white/60 leading-relaxed">
              Systems Beyond helps make those interactions visible, formal and useful.
            </p>
          </div>
          <div className="lg:col-span-7 flex items-center">
            <div className="w-full">
              <FlowDiagram />
            </div>
          </div>
        </div>
      </Section>

      {/* WHAT WE DO */}
      <Section className="py-24 lg:py-32">
        <Overline>What Systems Beyond does</Overline>
        <div className="mt-12 grid md:grid-cols-3 gap-px bg-silver/40 border border-silver/40">
          {whatWeDo.map((w, i) => (
            <Reveal key={w.title} delay={i * 100}>
              <div className="bg-white p-10 h-full group hover:bg-off-white transition-colors">
                <div className="w-11 h-11 border border-silver flex items-center justify-center group-hover:border-acid transition-colors">
                  <w.icon size={18} strokeWidth={1.5} className="text-ink" />
                </div>
                <h3 className="mt-7 text-2xl text-ink">{w.title}</h3>
                <p className="mt-3 text-sm text-dark-gray leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* THREE PATHS */}
      <Section className="pb-24 lg:pb-32">
        <div className="max-w-2xl">
          <Overline>Three paths</Overline>
          <h2 className="mt-6 text-3xl lg:text-5xl tracking-tight leading-tight font-medium text-ink">
            Choose the system you need to understand.
          </h2>
        </div>
        <div className="mt-14 grid lg:grid-cols-3 gap-6">
          {paths.map((p, i) => (
            <Reveal key={p.title} delay={i * 120}>
              <div className="border border-silver/50 p-8 h-full flex flex-col bg-off-white hover:-translate-y-1 hover:border-charcoal transition-all">
                <span className="font-mono-sci text-xs text-blue-gray">{p.tag}</span>
                <h3 className="mt-6 text-2xl text-ink">{p.title}</h3>
                <p className="mt-3 text-sm text-dark-gray leading-relaxed flex-1">{p.desc}</p>
                <Link to={p.to} className="mt-8 inline-flex items-center gap-2 text-[13px] font-medium text-ink group" data-testid={`path-cta-${i}`}>
                  {p.cta}
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform text-acid-dark" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* WHY */}
      <Section className="pb-24 lg:pb-32" grid>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Why Systems Beyond</Overline>
            <h2 className="mt-6 text-3xl lg:text-5xl tracking-tight leading-tight font-medium text-ink">
              Built for problems that do not fit simple boxes.
            </h2>
            <p className="mt-6 text-dark-gray leading-relaxed">
              Systems Beyond connects scientific reasoning, modeling, technology and implementation —
              so complex problems become structures you can actually work with.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-silver/40 border border-silver/40">
            {why.map((w, i) => (
              <Reveal key={w.t} delay={i * 90}>
                <div className="bg-white p-8 h-full">
                  <span className="w-2 h-2 bg-acid rounded-full inline-block" />
                  <h3 className="mt-5 text-lg text-ink leading-snug">{w.t}</h3>
                  <p className="mt-2 text-sm text-dark-gray leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* RESEARCH SPOTLIGHT */}
      <Section dark className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Overline dark>Current research spotlight</Overline>
            <h2 className="mt-6 text-3xl lg:text-5xl tracking-tight leading-tight font-medium">
              Beyond Life: modeling aging as a dynamical process.
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Our flagship independent research program develops computational models of somatic-cell
              aging. The project explores how biological mechanisms interact over time and how cells
              transition between healthy proliferation, adaptation, dysfunction, senescence and apoptosis.
            </p>
            <CTAButton to="/independent-research" variant="dark" className="mt-9" testId="spotlight-cta">Explore Beyond Life</CTAButton>
          </div>
          <div className="lg:col-span-7 min-w-0 w-full">
            <div className="border border-white/10 bg-charcoal-muted p-8 min-w-0 overflow-hidden">
              <span className="font-mono-sci text-[10px] text-white/40">FIG.02 — CELLULAR STATE TRANSITIONS</span>
              <div className="mt-6 min-w-0">
                <StateTransition dark />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CREDIBILITY */}
      <Section className="py-24 lg:py-32">
        <Overline>Credibility</Overline>
        <h2 className="mt-6 text-3xl lg:text-5xl tracking-tight leading-tight font-medium text-ink max-w-2xl">
          Led by scientific and technical experience.
        </h2>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="border border-silver/50 p-8 bg-off-white h-full">
              <div className="flex items-center gap-4">
                <img src={TEAM.david} alt="David Martinez, PhD" style={{ objectPosition: "center 18%" }} className="w-16 h-16 object-cover border border-silver grayscale" />
                <span className="font-mono-sci text-xs text-blue-gray">FOUNDER</span>
              </div>
              <h3 className="mt-5 text-2xl text-ink">David Martinez, PhD</h3>
              <p className="text-sm text-dark-gray mt-1">Founder and Head of Research</p>
              <p className="mt-5 text-sm text-dark-gray leading-relaxed">
                Complex systems scientist with experience in experimental science, mathematical
                modeling, AI and data-driven platforms.
              </p>
              <a href={SCHOLAR_URL} target="_blank" rel="noopener noreferrer" data-testid="scholar-link" className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium text-ink group">
                View Google Scholar
                <ArrowRight size={15} className="text-acid-dark group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="border border-silver/50 p-8 bg-off-white h-full">
              <div className="flex items-center gap-4">
                <img src={TEAM.rodolfo} alt="Rodolfo Leo, EngPhys" style={{ objectPosition: "center 18%" }} className="w-16 h-16 object-cover border border-silver grayscale" />
                <span className="font-mono-sci text-xs text-blue-gray">TECHNICAL LEAD</span>
              </div>
              <h3 className="mt-5 text-2xl text-ink">Rodolfo Leo, EngPhys</h3>
              <p className="text-sm text-dark-gray mt-1">Researcher and Technical Lead</p>
              <p className="mt-5 text-sm text-dark-gray leading-relaxed">
                Physics engineer, IT specialist and project manager, bridging modeling work with
                robust technical implementation.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16">
          <p className="overline">Institutions connected to our academic and professional experience</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-silver/40 border border-silver/40">
            {INSTITUTIONS.map((inst) => (
              <div key={inst.name} className="bg-white p-6 flex items-center justify-center min-h-[120px]">
                <img
                  src={inst.src}
                  alt={inst.name}
                  title={inst.name}
                  className="max-h-16 w-auto max-w-full object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section dark className="py-24 lg:py-32" grid>
        <div className="max-w-3xl">
          <Overline dark>Get in touch</Overline>
          <h2 className="mt-6 text-4xl lg:text-6xl tracking-tighter leading-[0.98] font-medium">
            Have a complex system to understand?
          </h2>
          <p className="mt-7 text-white/60 leading-relaxed max-w-xl">
            Whether you are building a research model, exploring aging biology, or improving a
            business ecosystem, Systems Beyond can help you clarify the structure, dynamics and
            next steps.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <CTAButton to="/contact" variant="accent" testId="final-book-consultation">Book a Consultation</CTAButton>
            <CTAButton to="/independent-research" variant="dark" testId="final-explore-research">Explore the Research</CTAButton>
          </div>
        </div>
      </Section>
    </div>
  );
}
